import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";

const ANDROID_PLAY_STORE = "https://play.google.com/store/apps/details?id=com.exora.club";
const DISMISS_KEY = "smartAppBannerDismissed";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent);
}

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Date.now() - ts < DISMISS_DURATION_MS) return true;
    localStorage.removeItem(DISMISS_KEY);
    return false;
  } catch {
    return false;
  }
}

/**
 * iOS uses the native Smart App Banner via <meta name="apple-itunes-app">.
 * This component only renders on Android to show a Play Store banner.
 */
export function SmartAppBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!isAndroid()) return;
    if (isDismissed()) return;
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, Date.now().toString()); } catch {}
  };

  const handleOpen = () => {
    const path = window.location.pathname + window.location.search;
    const cleanPath = path.replace(/^\//, "");
    const fallback = encodeURIComponent(ANDROID_PLAY_STORE);
    // Chrome on Android honours S.browser_fallback_url natively when the app isn't installed.
    const intentUrl =
      `intent://${cleanPath}#Intent;scheme=exora;package=com.exora.club;` +
      `S.browser_fallback_url=${fallback};end`;
    window.location.href = intentUrl;
    // Safety net for non-Chrome Android browsers
    setTimeout(() => { window.location.href = ANDROID_PLAY_STORE; }, 1500);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[200] border-b"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        background: "hsl(var(--charcoal))",
        borderColor: "hsl(var(--ivory) / 0.1)",
      }}
    >
      <div className="flex items-center gap-3 px-4 py-2">

        <img
          src="/exora-logo.png"
          alt=""
          className="w-10 h-10 rounded-xl flex-shrink-0"
          aria-hidden="true"
        />

        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-ivory truncate">Exora Club</p>
          <p className="text-[11px] text-ivory/40 truncate">Continue in the app</p>
        </div>

        <button
          onClick={handleOpen}
          className="flex-shrink-0 px-5 py-1.5 rounded-full text-[13px] font-bold uppercase tracking-wide transition-all active:scale-[0.97]"
          style={{
            background: "hsl(var(--gold))",
            color: "hsl(var(--charcoal))",
          }}
        >
          Open
        </button>
      </div>
    </div>
  );
}
